import { useState } from "react";
import { Eye, EyeSlash, EnvelopeSimple, User } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import type { AccountStepDraft, OnboardingLabels, StepError } from "./types";

export interface AccountStepProps {
  value: Omit<AccountStepDraft, "password">;
  password: string;
  onChange: (patch: Partial<Omit<AccountStepDraft, "password">>) => void;
  onPasswordChange: (password: string) => void;
  errors?: StepError[];
  labels: OnboardingLabels;
  className?: string;
}

function findError(errors: StepError[] | undefined, path: string) {
  return errors?.find((e) => e.path === path)?.message;
}

export function AccountStep({
  value,
  password,
  onChange,
  onPasswordChange,
  errors,
  labels,
  className,
}: AccountStepProps) {
  const [showPassword, setShowPassword] = useState(false);

  const emailError = findError(errors, "account.email");
  const passwordError = findError(errors, "account.password");
  const nameError = findError(errors, "account.displayName");
  const termsError = findError(errors, "account.acceptTerms");

  return (
    <div className={cn("space-y-5", className)}>
      <Field
        id="onboarding-email"
        label={labels.account.email}
        required
        error={emailError}
        leadingIcon={<EnvelopeSimple size={16} />}
      >
        <Input
          id="onboarding-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder={labels.account.emailPlaceholder}
          value={value.email}
          onChange={(e) => onChange({ email: e.target.value })}
          aria-invalid={!!emailError}
          className="h-14 pl-10 text-base"
        />
      </Field>

      <Field
        id="onboarding-password"
        label={labels.account.password}
        required
        error={passwordError}
      >
        <div className="relative">
          <Input
            id="onboarding-password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={labels.account.passwordPlaceholder}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            aria-invalid={!!passwordError}
            data-private="true"
            className="h-14 pr-12 text-base"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-md text-biz-foreground-muted hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
          >
            {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <PasswordStrengthMeter password={password} labels={labels.account.strength} className="mt-2" />
      </Field>

      <Field
        id="onboarding-name"
        label={labels.account.displayName}
        required
        error={nameError}
        leadingIcon={<User size={16} />}
      >
        <Input
          id="onboarding-name"
          type="text"
          autoComplete="name"
          placeholder={labels.account.displayNamePlaceholder}
          value={value.displayName}
          onChange={(e) => onChange({ displayName: e.target.value })}
          aria-invalid={!!nameError}
          className="h-14 pl-10 text-base"
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <LocaleToggle
          label={labels.account.localeTr}
          active={value.locale === "tr"}
          onClick={() => onChange({ locale: "tr" })}
        />
        <LocaleToggle
          label={labels.account.localeEn}
          active={value.locale === "en"}
          onClick={() => onChange({ locale: "en" })}
        />
      </div>

      <div className="space-y-3 pt-1">
        <CheckboxRow
          id="onboarding-terms"
          checked={value.acceptTerms}
          onCheckedChange={(c) => onChange({ acceptTerms: !!c })}
          label={labels.account.acceptTerms}
          error={termsError}
        />
        <CheckboxRow
          id="onboarding-marketing"
          checked={!!value.marketingOptIn}
          onCheckedChange={(c) => onChange({ marketingOptIn: !!c })}
          label={labels.account.marketingOptIn}
        />
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  leadingIcon?: React.ReactNode;
  children: React.ReactNode;
}

function Field({ id, label, required, error, leadingIcon, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 inline-flex items-center gap-1 text-sm font-medium text-biz-foreground">
        {label}
        {required && <span className="text-biz-danger">*</span>}
      </Label>
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted">
            {leadingIcon}
          </span>
        )}
        {children}
      </div>
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-biz-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface LocaleToggleProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function LocaleToggle({ label, active, onClick }: LocaleToggleProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex h-12 items-center justify-center rounded-md border text-sm font-medium transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
        active
          ? "border-biz-primary bg-biz-primary/10 text-biz-primary"
          : "border-biz-input bg-biz-background text-biz-foreground-muted hover:border-biz-primary/40 hover:text-biz-foreground",
      )}
    >
      {label}
    </button>
  );
}

interface CheckboxRowProps {
  id: string;
  checked: boolean;
  onCheckedChange: (c: boolean | "indeterminate") => void;
  label: string;
  error?: string;
}

function CheckboxRow({ id, checked, onCheckedChange, label, error }: CheckboxRowProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className={cn(
          "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
          error ? "border-biz-danger bg-biz-danger/5" : "border-biz-border hover:border-biz-primary/40",
        )}
      >
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="mt-0.5 h-5 w-5"
        />
        <span className="text-sm leading-snug text-biz-foreground">{label}</span>
      </label>
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-biz-danger">
          {error}
        </p>
      )}
    </div>
  );
}
